import { getVarConfig } from './environment';
import { cleanCookiesAndReload } from './cookiesUtils';

function isJsonContentType(contentType) {
  return contentType && contentType.includes('application/json');
}

function isHtmlResponse(text) {
  return typeof text === 'string' && text.trim().startsWith('<!DOCTYPE html');
}

export default async function fetchAPIV2({
  params = {},
  service,
  protocol = 'POST',
  callbackSuccess,
  callbackError,
  customBody,
  customHeaders = {},
  noContentType = false,
  customParams,
  skipAuth = false,
}) {
  const urlService = getVarConfig({ key: 'REACT_APP_SERVICES_URL' }) + service;

  try {
    /* -------------------- AUTH -------------------- */
    let bearer = null;

    if (!skipAuth) {
      const getBearerWithRetry = async (retries = 5, delay = 100) => {
        let token = localStorage.getItem('bearer');
        let attempts = 0;

        while (!token && attempts < retries) {
          await new Promise((res) => setTimeout(res, delay));
          token = localStorage.getItem('bearer');
          attempts++;
        }

        return token;
      };

      bearer = await getBearerWithRetry();
    }

    /* -------------------- HEADERS -------------------- */
    const headers = {
      ...customHeaders,
    };

    if (!skipAuth && bearer) {
      headers.Authorization = `Bearer ${bearer}`;
      headers['CSRF-Token'] = window.CSRF_TOKEN;
    }

    /* -------------------- BODY -------------------- */
    let body;

    if (protocol !== 'GET') {
      const rawBody = customParams ?? customBody ?? params;

      if (rawBody instanceof FormData) {
        body = rawBody;
        // ❗ NO poner Content-Type, el browser lo maneja
      } else if (typeof rawBody === 'string') {
        body = rawBody;
        if (!noContentType) {
          headers['Content-Type'] = 'application/json';
        }
      } else {
        body = JSON.stringify(rawBody);
        if (!noContentType) {
          headers['Content-Type'] = 'application/json';
        }
      }
    }

    const options = {
      method: protocol,
      headers,
      ...(body && { body }),
    };

    /* -------------------- FETCH -------------------- */
    const response = await fetch(urlService, options);

    const contentType = response.headers.get('content-type');
    const text = await response.text();

    /* -------------------- HTML DETECTION -------------------- */
    if (isHtmlResponse(text)) {
      const error = new Error('Unexpected HTML response (possible auth redirect or server error)');
      error.type = 'HTML_RESPONSE';
      error.status = response.status;
      error.raw = text;

      callbackError?.(error);
      throw error;
    }

    /* -------------------- PARSE -------------------- */
    let data = {};

    if (text) {
      if (isJsonContentType(contentType)) {
        try {
          data = JSON.parse(text);
        } catch (e) {
          const error = new Error('Invalid JSON response');
          error.type = 'INVALID_JSON';
          error.status = response.status;
          error.raw = text;

          callbackError?.(error);
          throw error;
        }
      } else {
        // respuesta no JSON pero válida
        data = text;
      }
    }

    /* -------------------- AUTH ERROR -------------------- */
    if (response.status === 401 && data?.error === 'invalid_token') {
      localStorage.clear();
      cleanCookiesAndReload();
    }

    /* -------------------- HTTP ERROR -------------------- */
    if (!response.ok) {
      const error = new Error('HTTP error');
      error.type = 'HTTP_ERROR';
      error.status = response.status;
      error.data = data;
      
      throw error;
    }

    /* -------------------- SUCCESS -------------------- */
    callbackSuccess?.(response.status , data);

    return {
      status: response.status,
      data,
    };
  } catch (error) {
    callbackError?.(error);
    throw error;
  }
}